/**
 * Public-page language hook — used on Landing / Pricing / Login / Register
 * etc. where there is no profile yet. Persists to localStorage and respects
 * the browser's preferred language on first visit.
 *
 * Inside the app (Board / Settings) we use `profile.language` instead, so
 * each child can have a different UI/voice language without affecting the
 * parent's marketing pages.
 */
import { useEffect, useState } from "react";
import { LANGUAGES } from "@/lib/i18n";

const STORAGE_KEY = "sonspeak.public_lang";
const SUPPORTED = new Set(LANGUAGES.map((l) => l.code));

function detectFromBrowser() {
  try {
    const candidates = [navigator.language, ...(navigator.languages || [])];
    for (const c of candidates) {
      if (!c) continue;
      const code = c.toLowerCase().split("-")[0];
      if (SUPPORTED.has(code)) return code;
    }
  } catch { /* ignore */ }
  return "en";
}

export function getPublicLang() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED.has(stored)) return stored;
  } catch { /* ignore */ }
  return detectFromBrowser();
}

export function setPublicLang(code) {
  if (!SUPPORTED.has(code)) return;
  try { localStorage.setItem(STORAGE_KEY, code); } catch { /* ignore */ }
  // Notify any listeners — useful so the banner + headers re-render together.
  try { window.dispatchEvent(new CustomEvent("sonspeak:public-lang-change", { detail: code })); } catch { /* ignore */ }
}

export function usePublicLang() {
  const [lang, setLang] = useState(() => getPublicLang());
  useEffect(() => {
    const onChange = (e) => setLang(e.detail);
    window.addEventListener("sonspeak:public-lang-change", onChange);
    return () => window.removeEventListener("sonspeak:public-lang-change", onChange);
  }, []);
  return [lang, setPublicLang];
}
